import { makeAutoObservable, runInAction } from "mobx"
import { notification, NotificationArgsProps } from "antd"
import ApiStore from "./ApiStore"
import dayjs from "dayjs"
import {
  Currency,
  OperationType,
  OperationCategory,
  PaymentType,
  FinancialTransaction,
  ErpEntityType,
  ErpHistoryEntry,
  ErpAttachment
} from "../api/FinanceApiClient"
import { t } from "i18next"

export interface PaginatedResult<T> {
  items: T[]
  totalCount: number
  pageNumber: number
  pageSize: number
  totalPages: number
  hasNext: boolean
  hasPrevious: boolean
}

export interface EntityVersion {
  id: number
  tableName: string
  columnName: string
  entityId: number
  userId: number | null
  user?: { firstName?: string; lastName?: string; userName?: string }
  oldValue?: string | null
  newValue?: string | null
  versionTag: string
  createdDate: string
  entityJson?: string | null
}

class FinanceStore {
  public currencies: Currency[] = []
  public operationTypes: OperationType[] = []
  public operationCategories: OperationCategory[] = []
  public paymentTypes: PaymentType[] = []
  public transactions: FinancialTransaction[] = []
  public overviewTransactions: FinancialTransaction[] = []
  public transactionVersions: EntityVersion[] = []
  public erpHistory: ErpHistoryEntry[] = []
  public erpAttachments: ErpAttachment[] = []
  public currentPage = 1
  public pageSize = 10
  public totalTransactions = 0
  public totalPages = 0
  public searchText = ""
  public selectedCurrency: number | null = null
  public selectedOperationType: number | null = null
  public selectedPaymentType: number | null = null
  public selectedOperationCategory: number | null = null
  public dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null = null
  public overviewDateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null = null
  public overviewSelectedCurrency: number | null = null
  public summaryTotals = { income: 0, expense: 0, balance: 0 }
  public isLoading = false
  public isOverviewLoading = false
  public error: string | null = null
  constructor(public apiStore: ApiStore) {
    makeAutoObservable(this)
  }
  get mainCurrency(): Currency | undefined {
    return this.currencies.find(c => c.isMain)
  }
  setLoading = (v: boolean) => (this.isLoading = v)
  setOverviewLoading = (v: boolean) => (this.isOverviewLoading = v)
  setError = (e: string | null) => (this.error = e)
  private toastError = (msg: string, err: any) => {
    console.error(msg, err)
    this.setError(err?.response?.data?.message || msg)
    notification.error({ message: t("Error"), description: err?.response?.data?.message || msg } as NotificationArgsProps)
  }
  private toastSuccess = (msg: string) => {
    notification.success({ message: t("Success"), description: msg } as NotificationArgsProps)
  }

  getCurrencies = async () => {
    this.setLoading(true)
    try {
      const r = await this.apiStore.CurrencyApiClient.getAll()
      runInAction(() => (this.currencies = r.data))
      return r.data
    } catch (e: any) {
      this.toastError(t("Failed to load currencies"), e)
      return []
    } finally {
      this.setLoading(false)
    }
  }
  addCurrency = async (dto: Partial<Currency>) => {
    this.setLoading(true)
    try {
      const r = await this.apiStore.CurrencyApiClient.add(dto)
      runInAction(() => {
        if (r.data.isMain) this.currencies.forEach(c => (c.isMain = false))
        this.currencies.push(r.data)
      })
      this.toastSuccess(t("Currency added successfully"))
      return r.data
    } catch (e: any) {
      this.toastError(t("Failed to add currency"), e)
      return null
    } finally {
      this.setLoading(false)
    }
  }
  updateCurrency = async (id: number, dto: Partial<Currency>) => {
    this.setLoading(true)
    try {
      await this.apiStore.CurrencyApiClient.update(id, dto)
      runInAction(() => {
        const i = this.currencies.findIndex(c => c.id === id)
        if (i !== -1) {
          if (dto.isMain) this.currencies.forEach(c => (c.isMain = false))
          this.currencies[i] = { ...this.currencies[i], ...dto } as Currency
        }
      })
      this.toastSuccess(t("Currency updated successfully"))
      return this.currencies.find(c => c.id === id) || null
    } catch (e: any) {
      this.toastError(t("Failed to update currency"), e)
      return null
    } finally {
      this.setLoading(false)
    }
  }
  deleteCurrency = async (id: number) => {
    this.setLoading(true)
    try {
      await this.apiStore.CurrencyApiClient.delete(id)
      runInAction(() => (this.currencies = this.currencies.filter(c => c.id !== id)))
      this.toastSuccess(t("Currency deleted successfully"))
      return true
    } catch (e: any) {
      this.toastError(t("Failed to delete currency"), e)
      return false
    } finally {
      this.setLoading(false)
    }
  }

  getOperationCategories = async () => {
    this.setLoading(true)
    try {
      const r = await this.apiStore.OperationTypeApiClient.getCategories()
      runInAction(() => (this.operationCategories = r.data))
      return r.data
    } catch (e: any) {
      this.toastError(t("Failed to load operation categories"), e)
      return []
    } finally {
      this.setLoading(false)
    }
  }
  getOperationTypes = async () => {
    this.setLoading(true)
    try {
      const r = await this.apiStore.OperationTypeApiClient.getAll()
      runInAction(() => (this.operationTypes = r.data))
      return r.data
    } catch (e: any) {
      this.toastError(t("Failed to load operation types"), e)
      return []
    } finally {
      this.setLoading(false)
    }
  }
  addOperationType = async (dto: Partial<OperationType>) => {
    this.setLoading(true)
    try {
      const r = await this.apiStore.OperationTypeApiClient.add(dto)
      runInAction(() => this.operationTypes.push(r.data))
      this.toastSuccess(t("Operation type added successfully"))
      return r.data
    } catch (e: any) {
      this.toastError(t("Failed to add operation type"), e)
      return null
    } finally {
      this.setLoading(false)
    }
  }
  updateOperationType = async (id: number, dto: Partial<OperationType>) => {
    this.setLoading(true)
    try {
      const current = this.operationTypes.find(o => o.id === id)
      if (!current) throw new Error("Operation type not found")
      await this.apiStore.OperationTypeApiClient.update(id, dto)
      runInAction(() => {
        const merged = { ...current, ...dto }
        this.operationTypes = this.operationTypes.map(o => (o.id === id ? merged : o))
      })
      this.toastSuccess(t("Operation type updated successfully"))
      return this.operationTypes.find(o => o.id === id) || null
    } catch (e: any) {
      this.toastError(t("Failed to update operation type"), e)
      return null
    } finally {
      this.setLoading(false)
    }
  }
  deleteOperationType = async (id: number) => {
    this.setLoading(true)
    try {
      await this.apiStore.OperationTypeApiClient.delete(id)
      runInAction(() => (this.operationTypes = this.operationTypes.filter(o => o.id !== id)))
      this.toastSuccess(t("Operation type deleted successfully"))
      return true
    } catch (e: any) {
      this.toastError(t("Failed to delete operation type"), e)
      return false
    } finally {
      this.setLoading(false)
    }
  }

  getPaymentTypes = async () => {
    this.setLoading(true)
    try {
      const r = await this.apiStore.PaymentTypeApiClient.getAll()
      runInAction(() => (this.paymentTypes = r.data))
      return r.data
    } catch (e: any) {
      this.toastError(t("Failed to load payment types"), e)
      return []
    } finally {
      this.setLoading(false)
    }
  }
  addPaymentType = async (dto: Partial<PaymentType>) => {
    this.setLoading(true)
    try {
      const r = await this.apiStore.PaymentTypeApiClient.add(dto)
      runInAction(() => this.paymentTypes.push(r.data))
      this.toastSuccess(t("Payment type added successfully"))
      return r.data
    } catch (e: any) {
      this.toastError(t("Failed to add payment type"), e)
      return null
    } finally {
      this.setLoading(false)
    }
  }
  updatePaymentType = async (id: number, dto: Partial<PaymentType>) => {
    this.setLoading(true)
    try {
      const current = this.paymentTypes.find(p => p.id === id)
      if (!current) throw new Error("Payment type not found")
      await this.apiStore.PaymentTypeApiClient.update(id, dto)
      runInAction(() => {
        const merged = { ...current, ...dto }
        this.paymentTypes = this.paymentTypes.map(p => (p.id === id ? merged : p))
      })
      this.toastSuccess(t("Payment type updated successfully"))
      return this.paymentTypes.find(p => p.id === id) || null
    } catch (e: any) {
      this.toastError(t("Failed to update payment type"), e)
      return null
    } finally {
      this.setLoading(false)
    }
  }
  deletePaymentType = async (id: number) => {
    this.setLoading(true)
    try {
      await this.apiStore.PaymentTypeApiClient.delete(id)
      runInAction(() => (this.paymentTypes = this.paymentTypes.filter(p => p.id !== id)))
      this.toastSuccess(t("Payment type deleted successfully"))
      return true
    } catch (e: any) {
      this.toastError(t("Failed to delete payment type"), e)
      return false
    } finally {
      this.setLoading(false)
    }
  }

  getOverviewTransactions = async (startDate?: string, endDate?: string, currencyId?: number | null) => {
    this.setOverviewLoading(true)
    try {
      const list: FinancialTransaction[] = []
      let page = 1
      const size = 500
      let hasNext = true
      while (hasNext) {
        const qs = new URLSearchParams()
        qs.append("pageNumber", String(page))
        qs.append("pageSize", String(size))
        if (typeof currencyId === "number") qs.append("currencyId", String(currencyId))
        if (startDate) qs.append("startDate", startDate)
        if (endDate) qs.append("endDate", endDate)
        const res = await this.apiStore.FinancialTransactionApiClient.getPaginated(qs.toString())
        list.push(...res.data.items)
        hasNext = res.data.hasNext
        page += 1
      }
      runInAction(() => {
        this.overviewTransactions = list
      })
      return list
    } catch (e: any) {
      this.toastError(t("Failed to load overview transactions"), e)
      return []
    } finally {
      this.setOverviewLoading(false)
    }
  }
  getOverviewTransactionsWithoutCurrency = async (startDate?: string, endDate?: string) => {
    this.setOverviewLoading(true)
    try {
      const qs = new URLSearchParams()
      qs.append("pageNumber", "1")
      qs.append("pageSize", "1000")
      if (startDate) qs.append("startDate", startDate)
      if (endDate) qs.append("endDate", endDate)
      qs.append("includingAllCurrencies", "true")
      const res = await this.apiStore.FinancialTransactionApiClient.getPaginated(qs.toString())
      runInAction(() => {
        this.overviewTransactions = res.data.items
      })
      return res.data.items
    } catch (e: any) {
      this.toastError(t("Failed to load overview transactions"), e)
      return []
    } finally {
      this.setOverviewLoading(false)
    }
  }
  getOverviewSummary = async (startDate?: string, endDate?: string, currencyId?: number | null) => {
    await this.getTransactionsSummary(
      undefined,
      currencyId ?? undefined,
      undefined,
      undefined,
      startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : undefined,
      undefined
    )
  }

  getTransactions = async () => {
    return this.getPaginatedTransactions(1, this.pageSize)
  }
  getTransactionsByDateRange = async (start: string, end: string) => {
    this.dateRange = [start ? dayjs(start) : null, end ? dayjs(end) : null]
    return this.getPaginatedTransactions(1, this.pageSize)
  }

  getTransactionsSummary = async (
    searchText?: string,
    currencyId?: number | null,
    operationTypeId?: number | null,
    paymentTypeId?: number | null,
    dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
    operationCategoryId?: number | null
  ) => {
    try {
      const qs = new URLSearchParams()
      if (searchText) qs.append("searchTerm", searchText)
      if (currencyId) qs.append("currencyId", currencyId.toString())
      if (operationTypeId) qs.append("operationTypeId", operationTypeId.toString())
      if (paymentTypeId) qs.append("paymentTypeId", paymentTypeId.toString())
      if (operationCategoryId) qs.append("operationCategoryId", operationCategoryId.toString())
      if (dateRange && dateRange[0]) qs.append("startDate", dateRange[0].format("YYYY-MM-DD"))
      if (dateRange && dateRange[1]) qs.append("endDate", dateRange[1].format("YYYY-MM-DD"))
      const res = await this.apiStore.FinancialTransactionApiClient.getSummary(qs.toString())
      runInAction(() => {
        const income = res.data.income ?? res.data.Income ?? res.data.totalIncome ?? res.data.TotalIncome ?? 0
        const expense = res.data.expense ?? res.data.Expense ?? res.data.totalExpense ?? res.data.TotalExpense ?? 0
        this.summaryTotals = { income, expense, balance: income - expense }
      })
    } catch (e: any) {
      this.toastError(t("Failed to load summary totals"), e)
    }
  }

  getPaginatedTransactions = async (page = 1, size = 10) => {
    if (page === 1) this.setLoading(true)
    try {
      const qs = new URLSearchParams()
      qs.append("pageNumber", String(page))
      qs.append("pageSize", String(size))
      if (this.searchText) qs.append("searchTerm", this.searchText)
      if (this.selectedCurrency) qs.append("currencyId", String(this.selectedCurrency))
      if (this.selectedOperationType) qs.append("operationTypeId", String(this.selectedOperationType))
      if (this.selectedPaymentType) qs.append("paymentTypeId", String(this.selectedPaymentType))
      if (this.selectedOperationCategory !== null && this.selectedOperationCategory !== undefined)
        qs.append("operationCategoryId", String(this.selectedOperationCategory))
      if (this.dateRange?.[0]) qs.append("startDate", this.dateRange[0]!.format("YYYY-MM-DD"))
      if (this.dateRange?.[1]) qs.append("endDate", this.dateRange[1]!.format("YYYY-MM-DD"))
      const res = await this.apiStore.FinancialTransactionApiClient.getPaginated(qs.toString())
      runInAction(() => {
        this.transactions = res.data.items
        this.totalTransactions = res.data.totalCount
        this.totalPages = res.data.totalPages
        this.currentPage = page
        this.pageSize = size
      })
      await this.getTransactionsSummary(
        this.searchText,
        this.selectedCurrency,
        this.selectedOperationType,
        this.selectedPaymentType,
        this.dateRange,
        this.selectedOperationCategory
      )
      return res.data
    } catch (e: any) {
      this.toastError(t("Failed to load transactions"), e)
      return null
    } finally {
      this.setLoading(false)
    }
  }

  getTransactionsForExport = async (
    searchText?: string,
    currencyId?: number | null,
    operationTypeId?: number | null,
    paymentTypeId?: number | null,
    dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null,
    operationCategoryId?: number | null
  ) => {
    this.setLoading(true)
    try {
      const qs = new URLSearchParams()
      qs.append("exportAll", "true")
      if (searchText) qs.append("searchTerm", searchText)
      if (currencyId) qs.append("currencyId", currencyId.toString())
      if (operationTypeId) qs.append("operationTypeId", operationTypeId.toString())
      if (paymentTypeId) qs.append("paymentTypeId", paymentTypeId.toString())
      if (operationCategoryId) qs.append("operationCategoryId", operationCategoryId.toString())
      if (dateRange && dateRange[0]) qs.append("startDate", dateRange[0].format("YYYY-MM-DD"))
      if (dateRange && dateRange[1]) qs.append("endDate", dateRange[1].format("YYYY-MM-DD"))
      const r = await this.apiStore.FinancialTransactionApiClient.getForExport(qs.toString())
      return r.data
    } catch (e: any) {
      this.toastError(t("Failed to export transactions"), e)
      return []
    } finally {
      this.setLoading(false)
    }
  }

  addTransaction = async (dto: Partial<FinancialTransaction>) => {
    this.setLoading(true)
    try {
      const r = await this.apiStore.FinancialTransactionApiClient.add(dto)
      this.toastSuccess(t("Transaction added successfully"))
      return r.data
    } catch (e: any) {
      this.toastError(t("Failed to add transaction"), e)
      return null
    } finally {
      this.setLoading(false)
    }
  }
  updateTransaction = async (id: number, dto: Partial<FinancialTransaction>) => {
    this.setLoading(true)
    try {
      await this.apiStore.FinancialTransactionApiClient.update(id, dto)
      this.toastSuccess(t("Transaction updated successfully"))
      return true
    } catch (e: any) {
      this.toastError(t("Failed to update transaction"), e)
      return null
    } finally {
      this.setLoading(false)
    }
  }
  deleteTransaction = async (id: number) => {
    this.setLoading(true)
    try {
      await this.apiStore.FinancialTransactionApiClient.delete(id)
      this.toastSuccess(t("Transaction deleted successfully"))
      runInAction(() => {
        this.transactions = this.transactions.filter(t => t.id !== id)
      })
      return true
    } catch (e: any) {
      this.toastError(t("Failed to delete transaction"), e)
      return false
    } finally {
      this.setLoading(false)
    }
  }

  // NOTE: these two are modal-scoped operations and must NOT toggle the global
  // isLoading. The Versions modal opens only after the fetch resolves, and the
  // rollback is followed by loadTransactions() which drives the list spinner.
  // Toggling the global flag here would spin the background list Table (and,
  // before the initial-load-only gate in TransactionManagement, took the whole
  // page over), unmounting the open modal mid-operation. Kept decoupled, in
  // line with getErpHistory/getErpAttachments.
  getTransactionVersions = async (txId: number) => {
    try {
      const r = await this.apiStore.FinancialTransactionApiClient.getVersions(txId)
      runInAction(() => (this.transactionVersions = r.data))
      return r.data as EntityVersion[]
    } catch (e: any) {
      this.toastError(t("Failed to load versions"), e)
      return []
    }
  }
  rollbackTransaction = async (txId: number, versionId: number) => {
    try {
      await this.apiStore.FinancialTransactionApiClient.rollback(txId, versionId)
      this.toastSuccess(t("Transaction rolled back successfully"))
      return true
    } catch (e: any) {
      this.toastError(t("Failed to roll back transaction"), e)
      return false
    }
  }

  // ERP audit history (ErpChangeLog-backed; distinct from the legacy transactionVersions above).
  getErpHistory = async (entityType: ErpEntityType, entityId: number) => {
    try {
      const history = await this.apiStore.FinancialTransactionApiClient.getHistory(entityType, entityId)
      runInAction(() => (this.erpHistory = history))
      return history
    } catch (e: any) {
      this.toastError(t("Failed to load history"), e)
      return []
    }
  }

  // Operation (FinancialTransaction) attachments.
  getErpAttachments = async (transactionId: number) => {
    try {
      const attachments = await this.apiStore.FinancialTransactionApiClient.getAttachments(transactionId)
      runInAction(() => (this.erpAttachments = attachments))
      return attachments
    } catch (e: any) {
      this.toastError(t("Failed to load attachments"), e)
      return []
    }
  }
  uploadErpAttachment = async (transactionId: number, file: File) => {
    try {
      const attachment = await this.apiStore.FinancialTransactionApiClient.uploadAttachment(transactionId, file)
      runInAction(() => {
        this.erpAttachments = [attachment, ...this.erpAttachments.filter(a => a.id !== attachment.id)]
      })
      return attachment
    } catch (e: any) {
      // Not toasted here: 409/400/502 carry a `code` the UI (Task 7) reads to show a specific message.
      throw e
    }
  }
  deleteErpAttachment = async (id: number) => {
    try {
      await this.apiStore.FinancialTransactionApiClient.deleteAttachment(id)
      runInAction(() => (this.erpAttachments = this.erpAttachments.filter(a => a.id !== id)))
      this.toastSuccess(t("Attachment deleted successfully"))
      return true
    } catch (e: any) {
      this.toastError(t("Failed to delete attachment"), e)
      return false
    }
  }
  getErpAttachmentDownloadUrl = (id: number) => this.apiStore.FinancialTransactionApiClient.downloadAttachmentUrl(id)
}

export default FinanceStore
